import type { PullRequestMetadata } from '@blume/web-test-model'
import { CircleCheck, MessageSquareText } from 'lucide-react'

import { Badge } from '@/ui/atoms/badge'
import { cn } from '@/lib/utils'

const statusLabel: Record<PullRequestMetadata['status'], string> = {
  draft: 'Draft',
  ready: 'Ready',
  merged: 'Merged',
  closed: 'Closed'
}

const statusClass: Record<PullRequestMetadata['status'], string> = {
  draft: 'border-status-neutral/35 bg-status-neutral-surface text-foreground',
  ready: 'border-status-info/35 bg-status-info-surface text-status-info',
  merged: 'border-status-pass/35 bg-status-pass-surface text-status-pass',
  closed: 'border-status-warn/35 bg-status-warn-surface text-status-warn'
}

export function PullRequestStatusBadge({ status }: { status: PullRequestMetadata['status'] }) {
  return (
    <Badge variant="outline" className={cn('font-medium', statusClass[status])}>
      {statusLabel[status]}
    </Badge>
  )
}

export function PullRequestChecksCounter({ checks }: { checks: PullRequestMetadata['checks'] }) {
  const complete = checks.total > 0 && checks.passed === checks.total
  return (
    <span
      aria-label={`${checks.passed} of ${checks.total} checks passed`}
      className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground"
    >
      <CircleCheck
        aria-hidden
        className={cn('size-3.5', complete ? 'text-status-pass' : 'text-status-warn')}
      />
      <span className="tabular-nums">
        <strong className="font-medium text-foreground">{checks.passed}</strong>/{checks.total}{' '}
        checks passed
      </span>
    </span>
  )
}

export function PullRequestReviewCommentCounter({
  reviewThreads
}: {
  reviewThreads: PullRequestMetadata['reviewThreads']
}) {
  return (
    <span
      aria-label={`${reviewThreads.unresolved} of ${reviewThreads.total} review threads unresolved`}
      className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground"
    >
      <MessageSquareText
        aria-hidden
        className={cn(
          'size-3.5',
          reviewThreads.unresolved > 0 ? 'text-status-warn' : 'text-status-pass'
        )}
      />
      <span className="tabular-nums">
        <strong className="font-medium text-foreground">{reviewThreads.unresolved}</strong>/
        {reviewThreads.total} unresolved threads
      </span>
    </span>
  )
}

export function PullRequestGitHubFacts({ metadata }: { metadata: PullRequestMetadata }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      <PullRequestChecksCounter checks={metadata.checks} />
      <PullRequestReviewCommentCounter reviewThreads={metadata.reviewThreads} />
    </div>
  )
}
